import { h, Component } from 'preact';
import util from 'preact-util';
import { route } from 'preact-router';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';

function typeIcon(type = '') {
    if (/liked/i.test(type)) {
        return 'fa-duotone fa-heart';
    }
    if (/comment/i.test(type)) {
        return 'fa-duotone fa-comment';
    }
    if (/mention/i.test(type)) {
        return 'fa-duotone fa-at';
    }
    if (/article/i.test(type)) {
        return 'fa-duotone fa-newspaper';
    }
    if (/workout/i.test(type)) {
        return 'fa-duotone fa-person-running';
    }
    if (/track/i.test(type)) {
        return 'fa-duotone fa-route';
    }
    if (/team/i.test(type)) {
        return 'fa-duotone fa-users';
    }
    if (/trophy/i.test(type)) {
        return 'fa-duotone fa-trophy';
    }
    if (/support/i.test(type)) {
        return 'fa-duotone fa-life-ring';
    }
    if (/birthday/i.test(type)) {
        return 'fa-duotone fa-cake-candles';
    }
    if (/vaccine/i.test(type)) {
        return 'fa-duotone fa-syringe';
    }
    if (/checkout|sale/i.test(type)) {
        return 'fa-duotone fa-cart-shopping';
    }
    if (/follow/i.test(type)) {
        return 'fa-duotone fa-user-plus';
    }
    if (/dog/i.test(type)) {
        return 'fa-duotone fa-dog';
    }
    return 'fa-duotone fa-bell';
}

@observer
class NotificationsDrawer extends Component {
    loadAll = async () => {
        const { userStore } = this.props.stores;
        await userStore.getNotifications();
    }

    // A row may represent several backend-grouped notifications; collect every
    // unread id in it so one click marks the whole group as read.
    // Grouped rows carry their own copy as grouped[0], so grouped alone covers the row itself.
    getUnreadIds = (notification) => {
        const all = notification.grouped?.length > 0 ? notification.grouped : [notification];
        return all.filter(n => n && !n.read && n.id > 0).map(n => n.id);
    }

    onClickNotification = async (notification) => {
        const { userStore, appState } = this.props.stores;
        const ids = this.getUnreadIds(notification);
        if (ids.length > 0) {
            await userStore.markNotificationsAsRead({ ids });
            await userStore.getNotifications();
        }
        const { url } = notification;
        if (url) {
            if (/^https?:\/\//.test(url)) {
                window.open(url, '_blank');
            } else {
                appState.toggleDrawer(null, 1);
                appState.toggleDrawerRight(null, 1);
                route(url.replace(/^#/, ''));
            }
        }
    }

    markAllAsRead = async () => {
        const { userStore } = this.props.stores;
        const { notifications = [] } = userStore;
        const ids = [];
        notifications.forEach(n => ids.push(...this.getUnreadIds(n)));
        if (ids.length === 0) {
            return;
        }
        await userStore.markNotificationsAsRead({ ids });
        await userStore.getNotifications();
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const { userStore } = this.props.stores;
        const { user, notifications = [] } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const unreadCount = notifications.reduce((acc, n) => acc + this.getUnreadIds(n).length, 0);

        return (<>
            <div class='w-100 d-flex justify-content-center mt-5'>
                <div class='w-100' style='padding-top: 0px; padding-bottom: 0px;'>
                    <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                        <div class='d-flex flex-row align-items-center p-2 rounded-lg'>
                            <h5 class='ms-3 mb-0'>
                                <i class='fa-duotone fa-bell me-2' />
                                <Text id='userNotifications.title'>Notifications</Text>
                            </h5>
                            {unreadCount > 0 && <>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill ms-auto'
                                    onClick={this.markAllAsRead}
                                >
                                    <i class='fa-solid fa-check-double me-1' />
                                    <Text id='userNotifications.markAllAsRead'>Mark all as read</Text>
                                </button>
                            </>}
                        </div>
                    </div>

                    <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                        {notifications.length === 0 && <>
                            <div class='text-body-secondary text-center py-4'>
                                <i class='fa-duotone fa-bell-slash me-2' />
                                <Text id='userNotifications.empty'>No notifications the last 7 days.</Text>
                            </div>
                        </>}
                        {notifications.map(notification => {
                            const { addedBy, grouped } = notification;
                            const isUnread = this.getUnreadIds(notification).length > 0;
                            const groupCount = grouped?.length || 0;
                            const addedByName = `${addedBy?.firstname || ''} ${addedBy?.lastname || ''}`.trim();
                            return (
                                <div
                                    key={`notification-${notification.id}`}
                                    class='d-flex flex-row align-items-center my-1 p-2 rounded-lg'
                                    onClick={() => this.onClickNotification(notification)}
                                    style='cursor: pointer;'
                                >
                                    {addedBy && localUtil.hasImage({ user: addedBy }) ? <>
                                        <img src={localUtil.getImage({ user: addedBy })} alt='' class='rounded-circle bg-light flex-shrink-0' style='width:40px; height:40px; object-fit:cover;' />
                                    </> : <>
                                        <div class='rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0' style='width:40px; height:40px; font-size:1.2em;'>
                                            <i class={typeIcon(notification.type)} />
                                        </div>
                                    </>}
                                    <div class='d-flex flex-column ms-3 flex-grow-1' style='min-width: 0;'>
                                        <div class={isUnread ? 'fw-bold' : ''}>
                                            {notification.title}
                                            {groupCount > 1 && <>
                                                <span class='badge bg-secondary rounded-pill ms-2'>{groupCount}</span>
                                            </>}
                                        </div>
                                        {notification.message && <>
                                            <small class={isUnread ? '' : 'text-body-secondary'} style='white-space: normal; overflow-wrap: anywhere;'>
                                                {notification.message.length > 300 ? `${notification.message.substring(0, 300)}...` : notification.message}
                                            </small>
                                        </>}
                                        <small class='text-body-secondary'>
                                            {addedByName && <>
                                                <span class='fw-semibold'>{addedByName}</span>
                                                <span class='mx-1'>&middot;</span>
                                            </>}
                                            {localUtil.getTimeAgo(notification.date)}
                                        </small>
                                    </div>
                                    {isUnread && <>
                                        <div class='ms-2 flex-shrink-0'>
                                            <span class='bg-primary rounded-circle d-inline-block' style='width: 10px; height: 10px;' />
                                        </div>
                                    </>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>);
    }
}

export default NotificationsDrawer;
